import React from 'react';
import { Link } from 'react-router-dom';
import { usePageLastUpdated } from '../../hooks/usePageLastUpdated';
import '../projects/ProjectPage.css';
import './ResearchDetailPage.css';

const PAGE_LAST_UPDATED = { dateTime: '2011-12-13', displayDate: 'December 13, 2011' };

const LatexInMatlabTicksPage = () => {
  usePageLastUpdated(PAGE_LAST_UPDATED);

  const versionHistory = [
    { version: 'Beta 1', date: 'August 31, 2011', desc: 'Initial beta release.' },
    { version: 'Beta 2', date: 'October 23, 2011', desc: 'Fixed an issue where scaled negative axis labels would not display properly.' },
    { version: 'Beta 3', date: 'November 19, 2011', desc: 'Better support for multiple axes in the same figure with the possibility of selecting the plot axis. Support for axis tick labels in non-default location (top or left).' },
    { version: 'Beta 4', date: 'December 13, 2011', desc: 'Included font size support.' },
  ];

  return (
    <div className="project-page research-detail-page" id="top">
      <div className="box" style={{ marginBottom: 'var(--spacing-md)' }}>Beta</div>
      <h1 className="title">Adding LaTeX to Plot Tick Labels in Matlab</h1>

      <h2>Overview</h2>
      <p>
        Matlab plots generally support LaTeX markup for the various components, including axes labels, legends,
        annotations, etc. However, one of the few exceptions are the axis tick labels, that is the data values
        alongside each axis, regardless of whether they are set automatically or manually by the user. Since in many
        situations it becomes necessary to use LaTeX in the tick labels, either to include mathematical expressions
        such as fractions, or simply to maintain a uniform look-and-feel, the Matlab code on this page enables this
        functionality without any change to normal plotting functions.
      </p>

      <h2>Download</h2>
      <ul>
        <li><a href="/download/plotTickLatex2D.m">plotTickLatex2D.m</a> (M/14.7 KB)</li>
        <li><a href="/download/plotTickLatex2DExample.m">plotTickLatex2DExample.m</a> (M/3.05 KB)</li>
      </ul>
      <p>Beta 4 version as of December 13, 2011.</p>

      <h2>Usage</h2>
      <p>After you finished plotting, and for every axis, call the following function:</p>
      <div className="box-code"><pre>plotTickLatex2D;</pre></div>
      <p>or:</p>
      <div className="box-code"><pre>plotTickLatex2D('argument_name', argument_value, ...);</pre></div>
      <p>Currently, the following optional arguments are supported:</p>
      <table className="information">
        <tbody>
          <tr><th className="strong" style={{ width: '150px' }}>Argument name</th><th>Argument description</th></tr>
          <tr><td className="strong">'Axis'</td><td>A handle of the axis. The default is the current axis.</td></tr>
          <tr><td className="strong">'FontSize'</td><td>The font size of the tick labels. The default is the font size of the current or specified axis.</td></tr>
          <tr><td className="strong">'XLabelDy'</td><td>An argument of value double, representing a shift of the X-axis label on the vertical direction.</td></tr>
          <tr><td className="strong">'YLabelDx'</td><td>An argument of value double, representing a shift of the Y-axis label on the horizontal direction.</td></tr>
        </tbody>
      </table>

      <h2>Example</h2>
      <p>This example demonstrates the use of LaTeX in horizontal and vertical axes tick labels.</p>
      <ul>
        <li>The font changes from Helvetica to Computer Modern, the standard LaTeX font in Matlab.</li>
        <li>The labels on the vertical axis contain mathematical expressions, including fractions and symbols.</li>
      </ul>
      <div className="project-row" style={{ display: 'flex', gap: 'var(--spacing-lg)', flexWrap: 'wrap' }}>
        <div>
          <img src="/research/latex_in_matlab_ticks/latex_in_matlab_ticks_before_small.png" alt="Before" width={420} height={320} style={{ maxWidth: '100%' }} />
          <p className="figure">Before</p>
        </div>
        <div>
          <img src="/research/latex_in_matlab_ticks/latex_in_matlab_ticks_after_small.png" alt="After" width={420} height={320} style={{ maxWidth: '100%' }} />
          <p className="figure">After</p>
        </div>
      </div>

      <h2>Limitations</h2>
      <p>The current version is limited to two-dimensional plots; three-dimensional plots are not yet supported.</p>

      <h2>Version History</h2>
      <table className="information">
        <tbody>
          <tr><th style={{ width: '150px' }}>Version</th><th className="strong" style={{ width: '150px' }}>Date</th><th>Description</th></tr>
          {versionHistory.map((v) => (
            <tr key={v.version}><td>{v.version}</td><td className="strong">{v.date}</td><td>{v.desc}</td></tr>
          ))}
        </tbody>
      </table>

      <p style={{ marginTop: 'var(--spacing-lg)' }}>
        <Link to="/research">← Research</Link>
      </p>
      <p className="update">Published: August 31, 2011. Last updated: December 13, 2011</p>
    </div>
  );
};

export default LatexInMatlabTicksPage;
